import { Avatar } from "@heroui/react";
import { memo, type ReactNode } from "react";

type PresetPillProps = {
  label: string;
  /** Visual hint only — parent decides whether to mark selected. */
  selected?: boolean;
  onPress: () => void;
  size?: "sm" | "md";
  /** Emoji or icon node (h-4 w-4). Ignored when imageUrl is set. */
  leading?: ReactNode;
  /** Optional thumbnail rendered as Avatar before the label. */
  imageUrl?: string;
  /** Optional toggle semantics. Default: trigger (no aria-pressed). */
  ariaPressed?: boolean;
  disabled?: boolean;
  className?: string;
  testId?: string;
};

// Spec: min touch height 32px for sm, 36px for md.
const sizeClasses = {
  sm: "h-8 px-2.5 text-xs",
  md: "h-9 px-3 text-sm",
} as const;

const PresetPillImpl = ({
  label,
  selected,
  onPress,
  size = "md",
  leading,
  imageUrl,
  ariaPressed,
  disabled,
  className,
  testId,
}: PresetPillProps) => (
  <button
    type="button"
    onClick={onPress}
    disabled={disabled}
    data-selected={selected}
    data-disabled={disabled || undefined}
    data-testid={testId}
    aria-pressed={ariaPressed}
    aria-disabled={disabled}
    className={`group/pill inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border font-medium transition-[background-color,border-color,transform] duration-150 active:scale-[0.96] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--brand-primary))] focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 hover:enabled:data-[selected=false]:bg-[hsl(var(--brand-bg-foreground)/0.08)] hover:enabled:data-[selected=true]:bg-[hsl(var(--brand-primary)/0.18)] ${sizeClasses[size]} ${className ?? ""}`}
    style={{
      borderColor: selected
        ? "hsl(var(--brand-primary))"
        : "hsl(var(--brand-bg-foreground) / 0.15)",
      background: selected
        ? "hsl(var(--brand-primary) / 0.12)"
        : "hsl(var(--brand-bg-foreground) / 0.04)",
      color: selected ? "hsl(var(--brand-primary))" : "hsl(var(--brand-bg-foreground))",
    }}
  >
    {imageUrl ? (
      <Avatar size="sm" className="h-5 w-5">
        <Avatar.Image src={imageUrl} alt="" />
        <Avatar.Fallback>{label.slice(0, 1)}</Avatar.Fallback>
      </Avatar>
    ) : (
      leading && <span className="inline-flex">{leading}</span>
    )}
    <span>{label}</span>
  </button>
);

export const PresetPill = memo(PresetPillImpl);

export default PresetPill;
